(ns ductile.ui.scene.viewer
  (:require [clojure.string :as str]
            [ductile.ui.components :as ui]
            [ductile.ui.deframe :as deframe]
            [ductile.ui.router :as router]
            [ductile.ui.scene :as sc]))

(def viewport-presets
  {:desktop {:label "Desktop" :width "100%" :height 800}
   :tablet  {:label "Tablet"  :width  1024  :height 768}
   :phone   {:label "Phone"   :width   390  :height 844}
   :mde     {:label "MDE"     :width   360  :height 512}})

(def global-default-viewport :desktop)

(def ns->default-viewport
  {'ductile.ui.workflow :mde})

(defn ns-default-viewport
  "Returns the default viewport for a given namespace name (string),
   falling back to :desktop when no ns-specific default is present."
  [ns-name]
  (let [ns-sym (some-> ns-name symbol)]
    (get ns->default-viewport ns-sym global-default-viewport)))

(defn ns-options []
  (->> (sc/all-ns->scenes) keys (map name) sort vec))

(defn scenes-for-ns [ns-name]
  (when ns-name (sc/scenes-for ns-name)))

(defn scene-option-label [scene]
  (:id scene))

(defn scene-viewport-preset [scene]
  (let [vp (:viewport scene)]
    (when (and (keyword? vp) (contains? viewport-presets vp))
      vp)))

(defn navigate-to-ns! [new-ns]
  (router/navigate-to
   (router/path '[developer scenes :ns] {:ns new-ns})))

(defn navigate-to-scene! [ns-name maybe-id-kw]
  (if (some? maybe-id-kw)
    (router/navigate-to
     (router/path '[developer scenes :ns :id]
                  {:ns ns-name :id (name maybe-id-kw)}))
    (router/navigate-to
     (router/path '[developer scenes :ns] {:ns ns-name}))))

(defn toolbar
  [{:keys [ns-name scene-id preset-id set-preset-id]}]
  (let [ns-list (ns-options)
        scenes  (vec (or (scenes-for-ns ns-name) []))
        scene-choices
        (cond-> [{:ui/value nil :ui/label "All scenes"}]
          (seq scenes)
          (into (map (fn [s]
                       {:ui/value (name (:id s))
                        :ui/label (scene-option-label s)}))
                scenes))]
    [:div {:class "flex gap-4 border rounded-lg p-3 bg-gray-50"}
     ;; Namespace select
     [:div {:class "flex items-center gap-2"}
      [:label {:class "text-xs"} "Namespace"]
      [ui/select {:value (or ns-name "")
                  :on-change (fn [new-ns]
                               (when (seq new-ns)
                                 (navigate-to-ns! new-ns)))
                  :class "pr-4"
                  :placeholder "Select ns…"
                  :choices (map (fn [n] {:ui/value n :ui/label n}) ns-list)}]]
     ;; Scene select
     [:div {:class "flex items-center gap-2"}
      [:label {:class "text-xs"} "Scene"]
      [ui/select {:value (some-> scene-id name)
                  :on-change (fn [v]
                               (if (seq v)
                                 (navigate-to-scene! ns-name (keyword v))
                                 (navigate-to-scene! ns-name nil)))
                  :disabled (not (seq scenes))
                  :class "pr-4"
                  :placeholder "Select a scene…"
                  :choices scene-choices}]]
     ;; Viewport preset select
     [:div {:class "flex items-center gap-2"}
      [:label {:class "text-xs"} "Viewport"]
      [ui/select {:value (name preset-id)
                  :on-change (fn [v]
                               (let [id (keyword v)]
                                 (set-preset-id (if (contains? viewport-presets id)
                                                  id
                                                  global-default-viewport))))
                  :placeholder "Select a viewport…"
                  :choices (map (fn [[id {:keys [label]}]]
                                  {:ui/value (name id) :ui/label label})
                                viewport-presets)}]]]))

(defn scene-surface [{:keys [scene width height]}]
  [:div
   (when-let [title (scene-option-label scene)]
     [:div {:class "px-3 font-bold text-sm text-gray-600"} title])
   (when-let [doc (:doc scene)]
     [:div {:class "px-3 text-xs text-gray-600"} doc])
   [:div {:class (str
                  "mt-3 overflow-auto border border-gray-200 rounded-2xl bg-white shadow-xl p-3 "
                  "[container-type:inline-size] [container-name:scene] "
                  "[width:" width "px]"
                  " "
                  "[height:" height "px]")}
    ^{:key (:id scene)}
    [(:render-fn scene) (:params scene)]]])

(defn page [match]
  (let [path-params (:path-params match)
        ns-name     (:ns path-params)
        scene-id    (some-> (:id path-params) keyword)
        scene       (when (and ns-name scene-id)
                      (sc/scene-by-id ns-name scene-id))
        {:keys [preset-id]}
        @(deframe/!route-get ::state {:preset-id (or (scene-viewport-preset scene)
                                                     (ns-default-viewport ns-name)
                                                     global-default-viewport)})
        {:keys [width height]} (get viewport-presets preset-id)
        scenes-in-ns (or (scenes-for-ns ns-name) [])]
    [:div {:class "flex flex-col gap-3 p-4"}
     [toolbar {:ns-name      ns-name
               :scene-id     scene-id
               :preset-id    preset-id
               :set-preset-id #(deframe/route-assoc-in! [::state :preset-id] %)}]
     (cond
       ;; All scenes (no :id in route)
       (and ns-name (nil? scene-id))
       (if (seq scenes-in-ns)
         [:div {:class "mt-6 flex flex-col gap-6"}
          (for [scn scenes-in-ns]
            ^{:key (str "all-" (name (:id scn)))}
            [scene-surface {:scene scn :width width :height height}])]
         [:div {:class "text-gray-500"} "No scenes in this namespace."])

       ;; Single scene
       (and ns-name (some? scene))
       [:div {:class "mt-6"}
        [scene-surface {:scene scene :width width :height height}]]

       ns-name
       [:div {:class "text-gray-500 text-sm px-3"} "No scenes in this namespace."]

       :else
       [:div {:class "text-gray-500 text-sm px-3"} "Pick a namespace to begin."])]))
